/**
 * PartnerDashboardPage Component
 *
 * Dashboard for marketplace partners to manage their published applications.
 */

import React, { useState } from "react";
import { ArrowPathIcon, PlusIcon, PencilIcon, ArrowUpCircleIcon, TrashIcon, EyeIcon, TagIcon, ArrowUturnUpIcon } from "@heroicons/react/24/outline";
import { DashboardLayout } from "../../../components/layout/DashboardLayout";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { Alert } from "../../../components/ui/Alert";
import { PublishAppModal } from "../components/PublishAppModal";
import { AddVersionModal } from "../components/AddVersionModal";
import { usePublishedListings, useUnpublishFromMarketplace, useRepublishToMarketplace, usePartnerStatus } from "../hooks/usePublishedListings";
import { useApplications } from "../../applications/hooks/useApplications";
import { useAuth } from "../../../contexts/AuthContext";
import { useLayoutProps } from "../../../hooks/useLayoutProps";
import type { MarketplaceListing } from "../../../types/marketplace.types";
import type { ApplicationSanitized } from "../../../types/application.types";
import { ApplicationDeploymentStatus } from "../../../types/application.types";
import { MARKETPLACE_CATEGORIES } from "../../../types/marketplace.types";
import { getErrorMessage } from "../../../lib/utils/errors";

/**
 * Get deployment status badge variant and text
 */
const getDeploymentStatusInfo = (status: string): { variant: "default" | "success" | "warning" | "error"; text: string } => {
  switch (status) {
    case ApplicationDeploymentStatus.DEPLOYED:
      return { variant: "success", text: "Ready to publish" };
    case ApplicationDeploymentStatus.DEPLOYING:
      return { variant: "warning", text: "Deploying..." };
    case ApplicationDeploymentStatus.PENDING:
      return { variant: "default", text: "Not deployed" };
    case ApplicationDeploymentStatus.FAILED:
      return { variant: "error", text: "Deploy failed" };
    default:
      return { variant: "default", text: status };
  }
};

/**
 * Get display label for a category
 */
const getCategoryLabel = (category: string): string => {
  const found = MARKETPLACE_CATEGORIES.find((c) => c.value === category);
  return found?.label || category;
};

/**
 * Format date for display
 */
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * PartnerDashboardPage component
 */
export const PartnerDashboardPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [showPublishModal, setShowPublishModal] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<ApplicationSanitized | null>(null);
  const [showAddVersionModal, setShowAddVersionModal] = useState<boolean>(false);
  const [selectedListing, setSelectedListing] = useState<MarketplaceListing | null>(null);

  const tenantId = tenant?.tenantId || "";

  // Fetch partner status from backend (stored in admin-api table)
  const { data: partnerStatusData, isLoading: isLoadingPartnerStatus } = usePartnerStatus(tenantId);

  const isPartner = partnerStatusData?.isPartner || false;

  const { data: publishedListings = [], isLoading, error, refetch } = usePublishedListings(tenantId);

  const { data: applications = [] } = useApplications(tenantId);

  const unpublishMutation = useUnpublishFromMarketplace();
  const republishMutation = useRepublishToMarketplace();

  // Filter applications that are not already published
  const publishedAppIds = new Set(publishedListings.map((l) => l.sourceApplicationId));
  const unpublishedApps = applications.filter((app) => !publishedAppIds.has(app.applicationId));

  const handlePublish = (app: ApplicationSanitized) => {
    setSelectedApp(app);
    setShowPublishModal(true);
  };

  const handleAddVersion = (listing: MarketplaceListing) => {
    setSelectedListing(listing);
    setShowAddVersionModal(true);
  };

  const handleUnpublish = async (listing: MarketplaceListing) => {
    if (!confirm(`Are you sure you want to unpublish "${listing.name}"? Existing installations will continue to work.`)) {
      return;
    }

    try {
      await unpublishMutation.mutateAsync({ tenantId, listingId: listing.listingId });
    } catch (err) {
      console.error("Unpublish failed:", err);
    }
  };

  const handleRepublish = async (listing: MarketplaceListing) => {
    if (!confirm(`Are you sure you want to republish "${listing.name}" to the marketplace?`)) {
      return;
    }

    try {
      await republishMutation.mutateAsync({ tenantId, listingId: listing.listingId });
    } catch (err) {
      console.error("Republish failed:", err);
    }
  };

  const handleViewListing = (listing: MarketplaceListing) => {
    window.location.href = `/app/marketplace/${listing.listingId}`;
  };

  // Loading partner status
  if (isLoadingPartnerStatus) {
    return (
      <DashboardLayout {...layoutProps}>
        <div className="text-center py-12 card rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
          <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--color-text-muted)' }} />
          <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>Checking partner status...</p>
        </div>
      </DashboardLayout>
    );
  }

  // Not a partner - show upgrade prompt
  if (!isPartner) {
    return (
      <DashboardLayout {...layoutProps}>
        <div className="text-center py-12 card rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
          <TagIcon className="h-12 w-12 mx-auto" style={{ color: 'var(--color-text-muted)' }} />
          <h3 className="mt-4 text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>Become a Partner</h3>
          <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
            Partner status is required to publish applications to the marketplace. Contact your platform administrator to become a marketplace
            partner.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Partner Dashboard</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>Manage your published marketplace applications</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Published Apps</p>
            <p className="mt-2 text-3xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>{publishedListings.filter((l) => l.status === "published").length}</p>
          </div>
          <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Total Installs</p>
            <p className="mt-2 text-3xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {publishedListings.reduce((sum, l) => sum + l.installCount, 0).toLocaleString()}
            </p>
          </div>
          <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Available to Publish</p>
            <p className="mt-2 text-3xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>{unpublishedApps.length}</p>
          </div>
        </div>

        {/* Publish New App */}
        {unpublishedApps.length > 0 && (
          <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Publish a New Application</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>Only deployed applications can be published to the marketplace.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {unpublishedApps.slice(0, 6).map((app) => {
                const isDeployed = app.deploymentStatus === ApplicationDeploymentStatus.DEPLOYED;
                const statusInfo = getDeploymentStatusInfo(app.deploymentStatus);

                return (
                  <div
                    key={app.applicationId}
                    className={`p-4 border rounded-lg transition-colors ${
                      isDeployed ? "hover:border-primary-500" : ""
                    }`}
                    style={{ borderColor: 'var(--color-border)', ...(!isDeployed ? { backgroundColor: 'var(--color-bg-sunken)' } : {}) }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDeployed ? "bg-primary-100" : ""}`} style={!isDeployed ? { backgroundColor: 'var(--color-bg-sunken)' } : undefined}>
                          <span className={`font-semibold ${isDeployed ? "text-primary-600" : ""}`} style={!isDeployed ? { color: 'var(--color-text-secondary)' } : undefined}>
                            {app.applicationName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: isDeployed ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>{app.applicationName}</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{app.applicationCode}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <Badge variant={statusInfo.variant}>{statusInfo.text}</Badge>
                      <Button
                        size="sm"
                        onClick={() => handlePublish(app)}
                        disabled={!isDeployed}
                        title={!isDeployed ? "Application must be deployed first" : undefined}
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Publish
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Published Listings */}
        <div className="card rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Published Listings</h2>
          </div>

          {isLoading && (
            <div className="text-center py-12">
              <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--color-text-muted)' }} />
              <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>Loading listings...</p>
            </div>
          )}

          {!isLoading && publishedListings.length === 0 && (
            <div className="text-center py-12">
              <TagIcon className="h-12 w-12 mx-auto" style={{ color: 'var(--color-text-muted)' }} />
              <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>No published listings yet</p>
            </div>
          )}

          {!isLoading && publishedListings.length > 0 && (
            <table className="min-w-full divide-y" style={{ borderColor: 'var(--color-border)' }}>
              <thead style={{ backgroundColor: 'var(--color-bg-sunken)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>Listing</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>Installs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}>
                {publishedListings.map((listing) => {
                  const latestVersion = listing.availableVersions?.[listing.availableVersions.length - 1];

                  return (
                    <tr key={listing.listingId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {listing.iconUrl ? (
                            <img src={listing.iconUrl} alt={listing.name} className="w-10 h-10 rounded-lg object-cover mr-3" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
                              <span className="text-primary-600 font-semibold">{listing.name.charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{listing.name}</div>
                            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Created {formatDate(listing.createdAt)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--color-text-secondary)' }}>{getCategoryLabel(listing.category)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--color-text-primary)' }}>{latestVersion?.version || "?"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--color-text-primary)' }}>{listing.installCount.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={listing.status === "published" ? "success" : "secondary"}>{listing.status}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button variant="ghost" size="sm" onClick={() => handleViewListing(listing)} title="View in marketplace">
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleAddVersion(listing)} title="Add new version">
                            <ArrowUpCircleIcon className="h-4 w-4" />
                          </Button>
                          {listing.status === "published" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnpublish(listing)}
                              title="Unpublish"
                              disabled={unpublishMutation.isPending}
                            >
                              <TrashIcon className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                          {listing.status === "unpublished" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRepublish(listing)}
                              title="Republish to marketplace"
                              disabled={republishMutation.isPending}
                            >
                              <ArrowUturnUpIcon className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Modals */}
        {showPublishModal && selectedApp && (
          <PublishAppModal
            isOpen={showPublishModal}
            onClose={() => {
              setShowPublishModal(false);
              setSelectedApp(null);
            }}
            application={selectedApp}
            tenantId={tenantId}
          />
        )}

        {showAddVersionModal && selectedListing && (
          <AddVersionModal
            isOpen={showAddVersionModal}
            onClose={() => {
              setShowAddVersionModal(false);
              setSelectedListing(null);
            }}
            listing={selectedListing}
            tenantId={tenantId}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default PartnerDashboardPage;
